import { useEffect, useState } from "react";
import DrawerLateral from "./DrawerLateral";
import Button from "../../../componentes/button/button";
import { CustomDropdown } from "../../../componentes/dropdown/CustomDropdown";
import AtribuicaoEtapaConfigService from "../../../services/resource/atribuicaoEtapaConfigService";
import GrupoService from "../../../services/resource/grupoService";
import { etapaLabel } from "../../../utils/solicitacoes/processoRegistry";
import { humanizarEtapa } from "../../../utils/solicitacoes/statusConfig";

const atribuicaoService = new AtribuicaoEtapaConfigService();
const grupoService = new GrupoService();

// Painel único e reutilizável de encaminhamento: aberto depois que os campos da etapa já
// validaram, mostra um resumo da ação + quem vai receber a próxima etapa (auto-descoberto via
// preview do backend) e só then dispara a submissão real. Usado tanto na criação quanto em
// qualquer ação de etapa existente — nunca embutido nos formulários.
export default function EncaminhamentoSidebar({ isOpen, carregando, preview, contexto, onCancelar, onConfirmar }) {
    const [usuarioEscolhidoId, setUsuarioEscolhidoId] = useState(null);
    const [usuarios, setUsuarios] = useState([]);
    const [carregandoUsuarios, setCarregandoUsuarios] = useState(false);

    // USUARIO_DO_GRUPO tem um grupo fixo por etapa (busca pelos membros elegíveis da própria
    // configuração). GRUPO_POR_CAMPO já resolveu o grupo variável no preview (preview.grupoId) —
    // mesma exigência de escolher uma pessoa, só que os membros vêm direto do grupo resolvido.
    const precisaEscolherUsuario = preview?.tipoAtribuicao === "USUARIO_DO_GRUPO" || preview?.tipoAtribuicao === "GRUPO_POR_CAMPO";

    useEffect(() => {
        setUsuarioEscolhidoId(null);
        if (!precisaEscolherUsuario) {
            setUsuarios([]);
            return;
        }

        if (preview.tipoAtribuicao === "GRUPO_POR_CAMPO") {
            if (!preview.grupoId) {
                setUsuarios([]);
                return;
            }
            setCarregandoUsuarios(true);
            grupoService.buscarGrupo(preview.grupoId)
                .then(res => setUsuarios(res.data.usuarios))
                .catch(() => setUsuarios([]))
                .finally(() => setCarregandoUsuarios(false));
            return;
        }

        if (!contexto?.tipoProcesso || !preview?.proximaEtapaCodigo) {
            setUsuarios([]);
            return;
        }
        setCarregandoUsuarios(true);
        atribuicaoService.listarUsuariosElegiveis(contexto.tipoProcesso, preview.proximaEtapaCodigo)
            .then(res => setUsuarios(res.data))
            .catch(() => setUsuarios([]))
            .finally(() => setCarregandoUsuarios(false));
    }, [precisaEscolherUsuario, preview?.tipoAtribuicao, preview?.grupoId, contexto?.tipoProcesso, preview?.proximaEtapaCodigo]);

    const semUsuariosElegiveis = precisaEscolherUsuario && !carregandoUsuarios && usuarios.length === 0;
    const podeConfirmar = !carregando && preview && (!precisaEscolherUsuario || !!usuarioEscolhidoId) && !semUsuariosElegiveis;

    const proximaEtapaLabel = preview?.proximaEtapaCodigo
        ? (etapaLabel(contexto?.tipoProcesso, preview.proximaEtapaCodigo) || humanizarEtapa(preview.proximaEtapaCodigo))
        : null;

    return (
        <DrawerLateral
            isOpen={isOpen}
            onClose={onCancelar}
            title="Encaminhar Solicitação"
            footer={
                <div className="flex justify-end gap-2">
                    <Button variant="outline" label="Cancelar" onClick={onCancelar} />
                    <Button
                        variant="blue"
                        label="Confirmar"
                        iconLeft="fa fa-paper-plane"
                        disabled={!podeConfirmar}
                        onClick={() => onConfirmar(usuarioEscolhidoId)}
                    />
                </div>
            }
        >
            {carregando && (
                <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
                    <i className="fa fa-spinner fa-spin mr-2" /> Carregando...
                </div>
            )}

            {!carregando && preview && (
                <>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <Campo label="Processo" valor={contexto?.processoLabel} />
                        {contexto?.numero && <Campo label="Número" valor={contexto.numero} />}
                        <Campo label="Etapa atual" valor={contexto?.etapaAtualLabel} />
                        <Campo label="Próxima etapa" valor={preview.encerra ? "Encerra a solicitação" : proximaEtapaLabel} />
                    </div>

                    <div className="border-t border-border pt-4 flex flex-col gap-3">
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Atribuição</h4>

                        {preview.encerra && (
                            <p className="text-sm text-muted-foreground">
                                Esta ação encerrará a solicitação — nenhum encaminhamento é necessário.
                            </p>
                        )}

                        {!preview.encerra && (preview.tipoAtribuicao === "USUARIO_DO_GRUPO" || preview.tipoAtribuicao === "GRUPO_POR_CAMPO") && (
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-foreground/80">Grupo</label>
                                <p className="text-sm text-foreground">{preview.grupoNome || "Ainda não é possível saber — preencha os campos acima"}</p>
                                {preview.tipoAtribuicao === "GRUPO_POR_CAMPO" && !preview.grupoNome ? (
                                    <span className="text-xs text-muted-foreground">
                                        O grupo depende de campos do formulário que ainda faltam preencher. Preencha-os e reabra este painel para escolher a pessoa.
                                    </span>
                                ) : (
                                    <>
                                        <label className="text-sm font-medium text-foreground/80 mt-2">
                                            Usuário <span className="text-destructive">*</span>
                                        </label>
                                        <CustomDropdown
                                            value={usuarioEscolhidoId}
                                            onChange={e => setUsuarioEscolhidoId(e.value)}
                                            options={usuarios}
                                            optionValue="id"
                                            optionLabel="nome"
                                            placeholder={carregandoUsuarios ? "Carregando..." : "Selecione um usuário"}
                                            disabled={carregandoUsuarios || semUsuariosElegiveis}
                                            filter
                                        />
                                        {semUsuariosElegiveis && (
                                            <span className="text-xs text-destructive">
                                                Nenhum usuário elegível encontrado — verifique a configuração de atribuições desta etapa.
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {!preview.encerra && (preview.tipoAtribuicao === "GRUPO"
                            || preview.tipoAtribuicao === "GRUPO_DO_TIPO_PRODUTO" || preview.tipoAtribuicao === "GRUPO_DA_FILIAL") && (
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-foreground/80">Destino</label>
                                <p className="text-sm text-foreground">{preview.grupoNome || "Grupo definido automaticamente"}</p>
                            </div>
                        )}

                        {!preview.encerra && preview.tipoAtribuicao === "USUARIO" && (
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-foreground/80">Destino</label>
                                <p className="text-sm text-foreground">{preview.usuarioFixoNome}</p>
                            </div>
                        )}

                        {!preview.encerra && !preview.tipoAtribuicao && (
                            <p className="text-sm text-destructive">
                                <i className="fa fa-exclamation-circle mr-1" />
                                Nenhuma configuração de atribuição encontrada para a próxima etapa.
                            </p>
                        )}
                    </div>
                </>
            )}
        </DrawerLateral>
    );
}

function Campo({ label, valor }) {
    return (
        <div className="min-w-0">
            <span className="block text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</span>
            <span className="block text-foreground font-medium truncate" title={valor || "-"}>{valor || "-"}</span>
        </div>
    );
}
